import { useCallback, useEffect, useMemo, useState } from 'react';
import './VideoSection.css';

function normalizeCategories(categories) {
  if (!Array.isArray(categories)) {
    return [];
  }

  return categories
    .map((category, categoryIndex) => {
      if (!category || typeof category !== 'object') {
        return null;
      }

      const videos = Array.isArray(category.videos)
        ? category.videos
            .map((video, videoIndex) => {
              if (!video || typeof video !== 'object' || !video.id || !video.thumbnail) {
                return null;
              }

              return {
                id: String(video.id),
                title: video.title ? String(video.title) : `未命名影片 ${videoIndex + 1}`,
                thumbnail: String(video.thumbnail),
              };
            })
            .filter(Boolean)
        : [];

      if (!videos.length) {
        return null;
      }

      return {
        id: category.id ? String(category.id) : `category-${categoryIndex}`,
        name: category.name ? String(category.name) : `分類 ${categoryIndex + 1}`,
        videos,
      };
    })
    .filter(Boolean);
}

function VideoSection({ categories, id }) {
  const normalizedCategories = useMemo(() => normalizeCategories(categories), [categories]);
  const [activeCategoryId, setActiveCategoryId] = useState(() => normalizedCategories[0]?.id ?? null);
  const [selectedVideo, setSelectedVideo] = useState(null);

  useEffect(() => {
    const firstId = normalizedCategories[0]?.id ?? null;
    setActiveCategoryId((prev) => (firstId && prev ? prev : firstId));
  }, [normalizedCategories]);

  const activeCategory = useMemo(() => {
    if (!activeCategoryId) {
      return normalizedCategories[0] ?? null;
    }

    return normalizedCategories.find((category) => category.id === activeCategoryId) ?? null;
  }, [activeCategoryId, normalizedCategories]);

  const handleSelectCategory = useCallback((categoryId) => {
    setActiveCategoryId(categoryId);
  }, []);

  const handleOpenVideo = useCallback((video, categoryName) => {
    setSelectedVideo({ ...video, categoryName });
  }, []);

  const handleCloseModal = useCallback(() => {
    setSelectedVideo(null);
  }, []);

  const modalOpen = Boolean(selectedVideo);

  useEffect(() => {
    if (!modalOpen) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        handleCloseModal();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = originalOverflow;
    };
  }, [modalOpen, handleCloseModal]);

  if (!normalizedCategories.length) {
    return null;
  }

  return (
    <section id={id} className="video-section" aria-labelledby="video-section-heading">
      <div className="video-section__header">
        <p className="video-section__eyebrow">影音專區</p>
        <h2 id="video-section-heading">至善30影音專區</h2>
        <p className="video-section__description">三十年的故事，一起回顧</p>
      </div>

      <div className="video-section__tabs" role="tablist" aria-label="影音分類">
        {normalizedCategories.map((category) => {
          const isActive = activeCategory?.id === category.id;
          return (
            <button
              key={category.id}
              type="button"
              role="tab"
              className={`video-section__tab${isActive ? ' is-active' : ''}`}
              aria-selected={isActive}
              onClick={() => handleSelectCategory(category.id)}
            >
              {category.name}
            </button>
          );
        })}
      </div>

      {activeCategory ? (
        <div
          role="tabpanel"
          className="video-section__panel"
          aria-label={`${activeCategory.name} 影片列表`}
        >
          <div className="video-section__grid">
            {activeCategory.videos.map((video) => (
              <article key={video.id} className="video-card">
                <button
                  type="button"
                  className="video-card__preview"
                  onClick={() => handleOpenVideo(video, activeCategory.name)}
                  aria-label={`播放 ${video.title}`}
                >
                  <span className="video-card__thumbnail">
                    <img src={video.thumbnail} alt={video.title} />
                    <span className="video-card__play">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true">
                        <path
                          d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                          fill="currentColor"
                        />
                        <path
                          d="M12 3.25a8.75 8.75 0 108.75 8.75A8.76 8.76 0 0012 3.25zm0 16a7.25 7.25 0 117.25-7.25A7.26 7.26 0 0112 19.25z"
                          fill="currentColor"
                        />
                      </svg>
                    </span>
                  </span>
                </button>
                <div className="video-card__body">
                  <h3>{video.title}</h3>
                </div>
              </article>
            ))}
          </div>
        </div>
      ) : null}

      {selectedVideo ? (
        <div className="video-modal" role="dialog" aria-modal="true" aria-labelledby="video-modal-title">
          <button type="button" className="video-modal__backdrop" onClick={handleCloseModal} aria-label="關閉影片" />
          <div className="video-modal__dialog">
            <div className="video-modal__header">
              <div>
                <p className="video-modal__category">{selectedVideo.categoryName}</p>
                <h3 id="video-modal-title">{selectedVideo.title}</h3>
              </div>
              <button type="button" className="video-modal__close" onClick={handleCloseModal} aria-label="關閉影片">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    d="M16.24 7.76a.75.75 0 00-1.06-1.06L12 9.88 8.82 6.7a.75.75 0 10-1.06 1.06L10.94 11l-3.18 3.18a.75.75 0 101.06 1.06L12 12.12l3.18 3.18a.75.75 0 001.06-1.06L13.06 11z"
                    fill="currentColor"
                  />
                </svg>
              </button>
            </div>
            <div className="video-modal__player">
              <div className="video-modal__frame">
                <div className="video-modal__media">
                  <iframe
                    src={`https://www.youtube.com/embed/${selectedVideo.id}?rel=0&modestbranding=1&autoplay=1`}
                    title={selectedVideo.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}

export default VideoSection;
